import { getNoteBech32Id, getReplaceableCoordinateFromEvent, isReplaceableEvent } from '@/lib/event'
import { resolveClientUrlPattern } from '@/lib/client-handlers'
import { getErrorMessage } from '@/lib/error'
import { toShare } from '@/lib/link'
import { simplifyUrl } from '@/lib/url'
import { mutePubkeySetAtom, unwrappedPinListAtom, favoriteRelaysWithSetsAtom } from '@/atoms/lists'
import { currentPubkeyAtom, canWriteAtom, unwrappedSignerAtom } from '@/atoms/session'
import { clearPinBuryState, getPinBuryState, setBuried, setPinned } from '@/atoms/pinBury'
import {
  attemptDelete,
  attemptDeleteFromRelay,
  determineTargetRelays,
  emitEventRemoved,
  getSeenEventRelayUrls,
  publishEventAndDisplay,
  removeEventFromCache
} from '@/services/client.service'
import {
  ArrowDown,
  Pin,
  ArrowUp,
  PinOff,
  Copy,
  Link,
  Code,
  SatelliteDish,
  Alert,
  Bell,
  BellOff,
  FilePenIcon,
  CloudUpload,
  Trash2,
  ArrowUpRight01Icon,
  Add01Icon,
  TranslateIcon
} from '@hugeicons/core-free-icons'
import { HugeiconsIcon, type IconSvgElement } from '@hugeicons/react'
import { Event } from '@nostr/tools/wasm'
import * as kinds from '@nostr/tools/kinds'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import RelayIcon from '../RelayIcon'
import { discardPendingEvent, pendingIdsAtom, savePendingEvent } from '@/atoms/pending'
import { groupedAtom, recentRelayFeedsAtom } from '@/atoms/feed'
import { usePendingPublishMap } from '@/lib/pendingPublish'
import { atom, useAtomValue } from 'jotai'
import { unwrap } from 'jotai/utils'
import { ResolvedSet } from '@nostr/gadgets/sets'
import { loadRelayList } from '@nostr/gadgets/lists'
import { pin, unpin } from '@/atoms/pin'
import { mutePrivately, mutePublicly, unmute } from '@/atoms/mutes'
import { toggleShowOriginal, translateAndSave, useNoteTranslation } from '@/atoms/translations'
import { now } from '@/lib/utils'

export type SubMenuAction = {
  label: React.ReactNode
  onClick: () => void
  className?: string
  separator?: boolean
}

export type MenuAction = {
  icon: IconSvgElement
  label: string
  onClick?: () => void
  className?: string
  separator?: boolean
  subMenu?: SubMenuAction[]
  disabled?: boolean
  title?: string
}

type UseMenuActionsProps = {
  event: Event
  closeDrawer: () => void
  openEditor: () => void
  showSubMenuActions: (subMenu: SubMenuAction[], title: string) => void
  setIsRawEventDialogOpen: (open: boolean) => void
  isSmallScreen: boolean
}

export type TBroadcastRelayItem = string | ResolvedSet<string> | { separator: true }

/**
 * Combined list of relays and relay sets available as republish targets.
 *
 * Sections separated by `{ separator: true }` markers:
 *   1. favorites the user has starred (kind:10012) + their relay sets
 *   2. window.hallway.universe.defaultFavoriteRelays
 *   3. window.hallway.universe.hardcodedFeeds
 *   4. last 3 relay feeds the user has visited (non-duplicate)
 *
 * Duplicates removed within each group; a relay that appears in multiple
 * groups only shows in the first group where it was added.
 */
const republishRelaysAtom = atom<Promise<TBroadcastRelayItem[]>>(async (get) => {
  const recent = get(recentRelayFeedsAtom).slice(0, 3)
  const favorites = await get(favoriteRelaysWithSetsAtom)
  const universe = window.hallway?.universe

  const result: TBroadcastRelayItem[] = []
  const seenRelays = new Set<string>()
  const seenSets = new Set<string>()

  const addRelay = (url: string) => {
    if (!url) return
    const key = url.replace(/\/+$/, '').toLowerCase()
    if (seenRelays.has(key)) return
    seenRelays.add(key)
    result.push(url)
  }
  const addSet = (relaySet: ResolvedSet<string>) => {
    const key = `${relaySet.pointer.pubkey}:${relaySet.pointer.identifier}`
    if (seenSets.has(key)) return
    seenSets.add(key)
    result.push(relaySet)
  }

  // Section 1: user favorites
  const favStart = result.length
  for (const f of favorites) {
    if (typeof f === 'string') addRelay(f)
    else addSet(f)
  }

  // Section 2: defaultFavoriteRelays
  const defaults = universe?.defaultFavoriteRelays ?? []
  if (defaults.length > 0 && result.length > favStart) result.push({ separator: true })
  for (const url of defaults) addRelay(url)

  // Section 3: hardcodedFeeds
  const hardcoded = universe?.hardcodedFeeds ?? []
  if (hardcoded.length > 0 && result.length > 0) result.push({ separator: true })
  for (const item of hardcoded) {
    if (item.relays) {
      for (const url of item.relays) addRelay(url)
    }
  }

  // Section 4: recent relay feeds visited
  if (recent.length > 0 && result.length > 0) result.push({ separator: true })
  for (const ref of recent) {
    if (typeof ref === 'string') addRelay(ref)
    else addSet(ref)
  }

  return result
})

const unwrappedRepublishRelaysAtom = unwrap(republishRelaysAtom)

export function useMenuActions({
  event,
  closeDrawer,
  openEditor,
  showSubMenuActions,
  setIsRawEventDialogOpen,
  isSmallScreen
}: UseMenuActionsProps) {
  const { t } = useTranslation()
  const pubkey = useAtomValue(currentPubkeyAtom)
  const canWrite = useAtomValue(canWriteAtom)
  const signer = useAtomValue(unwrappedSignerAtom)
  const mutePubkeySet = useAtomValue(mutePubkeySetAtom)
  const pinList = useAtomValue(unwrappedPinListAtom) ?? []
  const favorites = useAtomValue(unwrappedRepublishRelaysAtom)
  const grouped = useAtomValue(groupedAtom)
  const pendingIds = useAtomValue(pendingIdsAtom)
  const pendingPublishMap = usePendingPublishMap()
  const translation = useNoteTranslation(event)
  const isMuted = useMemo(() => mutePubkeySet.has(event.pubkey), [mutePubkeySet, event])
  const pinBuryState = useMemo(() => getPinBuryState(event.pubkey), [event.pubkey])
  const isPending = useMemo(
    () => pendingIds.includes(event.id) || pendingPublishMap.has(event.id),
    [pendingIds, pendingPublishMap, event.id]
  )

  const broadcastSubMenu: SubMenuAction[] = useMemo(() => {
    const items: SubMenuAction[] = []

    if (pubkey && favorites) {
      let needSeparator = false
      for (let index = 0; index < favorites.length; index++) {
        const fav = favorites[index]

        if (typeof fav === 'object' && 'separator' in fav) {
          needSeparator = items.length > 0
          continue
        }

        if (typeof fav === 'string') {
          items.push({
            label: (
              <div className="flex items-center gap-2 w-full">
                <RelayIcon url={fav} />
                <div className="flex-1 truncate text-left">{simplifyUrl(fav)}</div>
              </div>
            ),
            onClick: async () => {
              closeDrawer()
              await publishEventAndDisplay([fav], event)
              discardPendingEvent(event.id)
            },
            separator: needSeparator && items.length > 0
          })
          needSeparator = false
        } else {
          items.push({
            label: <div className="text-left truncate">{fav.title}</div>,
            onClick: async () => {
              closeDrawer()
              await publishEventAndDisplay(fav.items, event)
              discardPendingEvent(event.id)
            },
            separator: needSeparator && items.length > 0
          })
          needSeparator = false
        }
      }
    }

    items.push({
      label: (
        <div className="flex items-center gap-2 w-full">
          <HugeiconsIcon icon={Add01Icon} />
          <div className="flex-1 text-left">{t('Custom relay URL')}</div>
        </div>
      ),
      onClick: async () => {
        closeDrawer()
        const input = prompt(t('Enter relay URL (e.g. wss://relay.example.com)'))
        if (!input) return
        let url = input.trim()
        if (!/^wss?:\/\//i.test(url)) {
          url = 'wss://' + url
        }
        await publishEventAndDisplay([url], event)
        discardPendingEvent(event.id)
      },
      separator: items.length > 0
    })

    return items
  }, [pubkey, favorites, event])

  const reportSubMenu: SubMenuAction[] = useMemo(() => {
    const types = ['nudity', 'malware', 'profanity', 'illegal', 'spam', 'impersonation', 'other']

    return types.map((reportType) => ({
      label: <div className="text-left truncate">{t(reportType)}</div>,
      onClick: () => {
        closeDrawer()
        const promise = (async () => {
          if (!signer) throw new Error('not signed in')

          const tags: string[][] = []
          if (event.kind === kinds.Metadata) {
            tags.push(['p', event.pubkey, reportType])
          } else {
            tags.push(['p', event.pubkey])
            tags.push(['e', event.id, reportType])
            if (isReplaceableEvent(event.kind)) {
              tags.push(['a', getReplaceableCoordinateFromEvent(event), reportType])
            }
          }

          const reportEvent = await signer.signEvent({
            kind: kinds.Report,
            content: '',
            tags,
            created_at: now()
          })

          const seenRelays = getSeenEventRelayUrls(event.id, event)
          const authorOutboxRelays = (await loadRelayList(event.pubkey)).items
            .filter((r) => r.write)
            .map((r) => r.url)
          const relays = Array.from(new Set([...seenRelays, ...authorOutboxRelays]))
          if (relays.length === 0) relays.push(...window.hallway.universe.bigRelayUrls)

          await publishEventAndDisplay(relays, reportEvent)
          await removeEventFromCache(event.id)
          emitEventRemoved(event.id)
        })()
        toast.promise(promise, {
          loading: t('Reporting...'),
          success: () => t('Reported successfully!'),
          error: (err: unknown) => t('Failed to report: {{error}}', { error: getErrorMessage(err) })
        })
      }
    }))
  }, [t, event, closeDrawer, signer])

  const deleteRelaySubMenu: SubMenuAction[] = useMemo(() => {
    if (!pubkey || event.pubkey !== pubkey) return []

    const items: SubMenuAction[] = getSeenEventRelayUrls(event.id, event).map((relay) => ({
      label: (
        <div className="flex items-center gap-2 w-full">
          <RelayIcon url={relay} />
          <div className="flex-1 truncate text-left">{simplifyUrl(relay)}</div>
        </div>
      ),
      onClick: () => {
        closeDrawer()
        const promise = attemptDeleteFromRelay(event, relay)
        toast.promise(promise, {
          loading: t('Deleting from {{relay}}...', { relay: simplifyUrl(relay) }),
          success: () => t('Deleted from {{relay}}', { relay: simplifyUrl(relay) }),
          error: (err: unknown) => t('Failed to delete: {{error}}', { error: getErrorMessage(err) })
        })
      }
    }))

    items.push({
      label: (
        <div className="flex items-center gap-2 w-full">
          <HugeiconsIcon icon={Add01Icon} />
          <div className="flex-1 text-left">{t('Custom relay URL')}</div>
        </div>
      ),
      onClick: () => {
        closeDrawer()
        const input = prompt(t('Enter relay URL (e.g. wss://relay.example.com)'))
        if (!input) return
        let url = input.trim()
        if (!/^wss?:\/\//i.test(url)) url = 'wss://' + url
        const promise = attemptDeleteFromRelay(event, url)
        toast.promise(promise, {
          loading: t('Deleting from {{relay}}...', { relay: simplifyUrl(url) }),
          success: () => t('Deleted from {{relay}}', { relay: simplifyUrl(url) }),
          error: (err: unknown) => t('Failed to delete: {{error}}', { error: getErrorMessage(err) })
        })
      },
      separator: items.length > 0
    })

    return items
  }, [t, event, pubkey, closeDrawer])

  const menuActions: MenuAction[] = useMemo(() => {
    const actions: MenuAction[] = []

    // Pin/Bury user actions (first block) - only when grouped notes is enabled
    if (grouped) {
      if (pinBuryState === 'pinned') {
        actions.push({
          icon: PinOff,
          label: t('Pin user'),
          onClick: () => {
            closeDrawer()
            clearPinBuryState(event.pubkey)
          }
        })
        actions.push({
          icon: ArrowDown,
          label: t('Bury user'),
          onClick: () => {
            closeDrawer()
            setBuried(event.pubkey)
          }
        })
      } else if (pinBuryState === 'buried') {
        actions.push({
          icon: ArrowUp,
          label: t('Unbury user'),
          onClick: () => {
            closeDrawer()
            clearPinBuryState(event.pubkey)
          }
        })
        actions.push({
          icon: Pin,
          label: t('Unpin user'),
          onClick: () => {
            closeDrawer()
            setPinned(event.pubkey)
          }
        })
      } else {
        actions.push({
          icon: Pin,
          label: t('Pin user'),
          onClick: () => {
            closeDrawer()
            setPinned(event.pubkey)
          }
        })
        actions.push({
          icon: ArrowDown,
          label: t('Bury user'),
          onClick: () => {
            closeDrawer()
            setBuried(event.pubkey)
          }
        })
      }
    }

    // Standard actions
    actions.push(
      ...([
        {
          icon: Copy,
          label: t('Copy event ID'),
          onClick: () => {
            navigator.clipboard.writeText(getNoteBech32Id(event))
            closeDrawer()
          },
          separator: grouped // Only add separator if pin/bury actions were shown
        },
        !isPending && {
          icon: Link,
          label: t('Copy share link'),
          onClick: () => {
            navigator.clipboard.writeText(toShare(getNoteBech32Id(event)))
            closeDrawer()
          }
        },
        typeof window.inference?.request === 'function' && {
          icon: TranslateIcon,
          label: translation
            ? translation.showOriginal
              ? t('Show translation')
              : t('Show original')
            : t('Translate'),
          disabled: translation?.inflight,
          onClick: () => {
            closeDrawer()
            if (translation) {
              toggleShowOriginal(event.id)
            } else {
              void translateAndSave(event)
            }
          }
        },
        ...(() => {
          const handlerRegistry = window.hallway.universe?.clientHandlers
          const fallbackHandlers = handlerRegistry?.fallback ?? []
          const kindHandlers = handlerRegistry?.byKind?.[event.kind] ?? []
          const supportedClients = [...kindHandlers, ...fallbackHandlers]
          if (supportedClients.length === 0) return []

          const subMenu = supportedClients.map((c) => {
            const href = resolveClientUrlPattern(c.urlPattern, event)
            return {
              label: c.name,
              onClick: () => {
                window.open(href, '_blank')
                closeDrawer()
              }
            }
          })

          return [
            {
              icon: ArrowUpRight01Icon,
              label: t('Open in another client'),
              subMenu,
              separator: true
            }
          ]
        })(),

        {
          icon: Code,
          label: t('View raw event'),
          onClick: () => {
            closeDrawer()
            setIsRawEventDialogOpen(true)
          },
          separator: true
        }
      ].filter(Boolean) as MenuAction[])
    )

    const isProtected = event.tags.some(([t]) => t === '-')
    if (!isProtected || event.pubkey === pubkey) {
      actions.push({
        icon: SatelliteDish,
        label: isPending ? t('Publish to ...') : t('Republish to ...'),
        onClick: isSmallScreen
          ? () =>
              showSubMenuActions(
                broadcastSubMenu,
                isPending ? t('Publish to ...') : t('Republish to ...')
              )
          : undefined,
        subMenu: broadcastSubMenu,
        separator: true
      })
    }

    if (canWrite && pubkey && event.pubkey !== pubkey) {
      actions.push({
        icon: Alert,
        label: t('Report'),
        className: 'text-destructive focus:text-destructive',
        onClick: isSmallScreen ? () => showSubMenuActions(reportSubMenu, t('Report')) : undefined,
        subMenu: reportSubMenu
      })
    }

    if (event.pubkey === pubkey && event.kind === kinds.ShortTextNote) {
      const pinned = pinList.includes(event.id)
      actions.push({
        icon: pinned ? PinOff : Pin,
        label: pinned ? t('Unpin from profile') : t('Pin to profile'),
        onClick: async () => {
          closeDrawer()
          await (pinned ? unpin(event) : pin(event))
        },
        disabled: !canWrite
      })
    }

    if (canWrite && pubkey && event.pubkey !== pubkey) {
      if (isMuted) {
        actions.push({
          icon: Bell,
          label: t('Unmute user'),
          onClick: () => {
            closeDrawer()
            unmute(event.pubkey)
          },
          className: 'text-destructive focus:text-destructive',
          separator: true
        })
      } else {
        actions.push(
          {
            icon: BellOff,
            label: t('Mute user privately'),
            onClick: () => {
              if (!signer?.nip04Decrypt) return
              closeDrawer()
              mutePrivately(event.pubkey)
            },
            className: 'text-destructive focus:text-destructive',
            separator: true,
            disabled: !signer?.nip04Decrypt,
            title: !signer?.nip04Decrypt
              ? t('Your login method does not support encryption')
              : undefined
          },
          {
            icon: BellOff,
            label: t('Mute user publicly'),
            onClick: () => {
              closeDrawer()
              mutePublicly(event.pubkey)
            },
            className: 'text-destructive focus:text-destructive'
          }
        )
      }
    }

    if (pubkey && event.pubkey === pubkey) {
      if (isPending) {
        actions.push({
          icon: FilePenIcon,
          label: t('Edit'),
          onClick: () => {
            closeDrawer()
            openEditor()
          },
          className: 'text-black focus:text-black dark:text-black dark:focus:text-black',
          disabled: !canWrite
        })
      }

      if (deleteRelaySubMenu.length > 0 && !isPending) {
        actions.push({
          icon: Trash2,
          label: t('Delete from ...'),
          onClick: isSmallScreen
            ? () => showSubMenuActions(deleteRelaySubMenu, t('Delete from specific relay'))
            : undefined,
          subMenu: deleteRelaySubMenu,
          className: 'text-destructive focus:text-destructive',
          disabled: !canWrite
        })
      }

      actions.push({
        icon: isPending ? CloudUpload : Trash2,
        label: isPending ? t('Publish this note') : t('Try to delete this note'),
        onClick: async () => {
          closeDrawer()
          if (isPending) {
            try {
              const relays = await determineTargetRelays(event)
              await publishEventAndDisplay(relays, event)
              discardPendingEvent(event.id)
            } catch {
              savePendingEvent(event)
            }
          } else {
            attemptDelete(event)
          }
        },
        className: 'text-destructive focus:text-destructive',
        disabled: !canWrite
      })
    }

    return actions
  }, [
    t,
    event,
    pubkey,
    canWrite,
    isMuted,
    isPending,
    isSmallScreen,
    broadcastSubMenu,
    reportSubMenu,
    deleteRelaySubMenu,
    pinList,
    pinBuryState,
    grouped,
    closeDrawer,
    openEditor,
    showSubMenuActions,
    setIsRawEventDialogOpen,
    mutePrivately,
    mutePublicly,
    unmute,
    setPinned,
    setBuried,
    clearPinBuryState,
    translation
  ])

  return menuActions
}
